import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button } from 'primereact/button';
import FormInputText from '../../components/form/FormInputText';
import FormInputNumber from '../../components/form/FormInputNumber';
import FormDropdown from '../../components/form/FormDropdown';
import FormMultiSelect from '../../components/form/FormMultiSelect';
import FormChips from '../../components/form/FormChips';
import FormCalendar from '../../components/form/FormCalendar';
import { SimpleBinaryExpression, Operator, FieldMetadata, OperatorInfo, DataType } from '../../models/expression';
import { EnumValueData } from '../../models/enum';
import * as commonService from '../../services/commonService';
import styles from './SimpleExpressionForm.module.css';

const stripFieldFormat = (value: string | null | undefined): string => {
  if (!value) return '';
  return value
    .trim()
    .replace(/^\[/, '')
    .replace(/\]$/, '')
    .replace(/^["']/, '')
    .replace(/["']$/, '')
    .trim();
};

const formatFieldForBackend = (value: string | null | undefined): string => {
  if (!value) return '';
  const trimmed = value.trim();

  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    return trimmed;
  }
  return `[${trimmed}]`;
};

const stripRightValueFormat = (value: string | null | undefined): string => {
  if (!value) return '';
  return value
    .trim()
    .replace(/^["']/, '')
    .replace(/["']$/, '')
    .trim();
};

const parseDateOnlyAsLocal = (rawValue: string): Date | string => {
  const match = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const y = parseInt(match[1], 10);
    const m = parseInt(match[2], 10) - 1;
    const d = parseInt(match[3], 10);
    const date = new Date(y, m, d);
    return isNaN(date.getTime()) ? rawValue : date;
  }
  const dateValue = new Date(rawValue);
  return isNaN(dateValue.getTime()) ? rawValue : dateValue;
};

const formatRightValueForBackend = (value: string | number | Date | null | undefined): string => {
  if (!value && value !== 0) return '';

  if (value instanceof Date) {
    return value.toISOString();
  }

  const stringValue = String(value);
  const trimmed = stringValue.trim();


  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    return trimmed.slice(1, -1).trim();
  }
  return trimmed;
};

interface SimpleExpressionFormProps {
  onSubmit?: (expression: SimpleBinaryExpression) => void;
  onCancel?: () => void;
  initialValue?: SimpleBinaryExpression;
  onChange?: (expression: SimpleBinaryExpression | null) => void;
  showDelete?: boolean;
  onAddGroup?: () => void;
  showAddGroup?: boolean;
  fieldMetadata?: FieldMetadata[];
  rightFieldMetadata?: FieldMetadata[];
  operators?: OperatorInfo[];
  allowCustomFields?: boolean;
}

interface FormValues {
  left: string;
  operator: string;
  right: string | number | string[] | Date;
}

const createValidationSchema = (isList: boolean): yup.ObjectSchema<FormValues> => {
  return yup.object().shape({
    left: yup.string().trim().required('Field is required'),
    operator: yup.string().required('Operator is required'),
    right: isList
      ? yup.array().of(yup.string().trim()).min(1, 'At least one value is required').required('Value is required')
      : yup.mixed<string | number | Date>().required('Value is required')
        .test('not-empty', 'Value is required', (value) => {
          if (typeof value === 'number') return true;
          if (value instanceof Date) return true;
          if (typeof value === 'string') return value.trim().length > 0;
          return false;
        }),
  }) as yup.ObjectSchema<FormValues>;
};

export const SimpleExpressionForm: FC<SimpleExpressionFormProps> = ({
  onSubmit: _onSubmit,
  onCancel,
  initialValue,
  onChange,
  showDelete = true,
  onAddGroup,
  showAddGroup = false,
  fieldMetadata,
  rightFieldMetadata,
  operators,
  allowCustomFields = false,
}) => {
  const defaultOperator = useMemo(() => {
    if (!operators || operators.length === 0) {
      return 'Equal';
    }
    
    let selectedDataType: DataType | null = null;
    if (initialValue?.left && fieldMetadata) {
      const leftLower = initialValue.left.toLowerCase();
      const field = fieldMetadata.find((f) => f.fieldName?.toLowerCase() === leftLower);
      selectedDataType = field?.dataType || null;
    }
    
    let filtered = operators.filter((op) => op.isLogical);
    if (selectedDataType !== null) {
      const dataType = selectedDataType;
      filtered = filtered.filter((op) => op.validDataTypes?.includes(dataType) ?? true);
    }
    
    return filtered.length > 0 ? filtered[0].operator : (operators[0]?.operator || 'Equal');
  }, [operators, initialValue?.left, fieldMetadata]);

  const initialIsList = useMemo(() => {
    if (!initialValue?.left || !fieldMetadata) return false;
    const strippedLeft = stripFieldFormat(initialValue.left).toLowerCase();
    const field = fieldMetadata.find((f) => f.fieldName?.toLowerCase() === strippedLeft);
    const isMultiValueOperator = initialValue?.operator === 'IsOneOf' || 
                                   initialValue?.operator === 'ContainsAny' || 
                                   initialValue?.operator === 'ContainsAll';
    return field?.isList || isMultiValueOperator || false;
  }, [initialValue?.left, initialValue?.operator, fieldMetadata]);

  const initialFieldMetadata = useMemo(() => {
    if (!initialValue?.left || !fieldMetadata) return null;
    const strippedLeft = stripFieldFormat(initialValue.left).toLowerCase();
    return fieldMetadata.find((f) => f.fieldName?.toLowerCase() === strippedLeft) || null;
  }, [initialValue?.left, fieldMetadata]);

  const getInitialRightValue = () => {
    if (!initialValue?.right || initialValue.right.length === 0) {
      return initialIsList ? [] : '';
    }
    
    if (initialIsList) {
      return initialValue.right.map(v => stripRightValueFormat(v));
    }
    
    let rawValue = stripRightValueFormat(initialValue.right[0]) || '';
    
    if (initialFieldMetadata && (initialFieldMetadata.dataType === DataType.Number || initialFieldMetadata.dataType === DataType.Integer)) {
      rawValue = rawValue.replace(/^\[/, '').replace(/\]$/, '').trim();
      const numValue = parseFloat(rawValue);
      return isNaN(numValue) ? rawValue : numValue;
    }
    
    if (initialFieldMetadata && (initialFieldMetadata.dataType === DataType.Date || initialFieldMetadata.dataType === DataType.DateTime)) {
      rawValue = rawValue.replace(/^\[/, '').replace(/\]$/, '').trim();
      return initialFieldMetadata.dataType === DataType.Date
        ? parseDateOnlyAsLocal(rawValue)
        : (() => { const d = new Date(rawValue); return isNaN(d.getTime()) ? rawValue : d; })();
    }
    
    if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
      const parsedDate = parseDateOnlyAsLocal(rawValue);
      if (parsedDate instanceof Date) return parsedDate;
    }
    return rawValue;
  };

  const {
    control,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(createValidationSchema(initialIsList)),
    mode: 'onChange',
    defaultValues: {
      left: initialFieldMetadata?.fieldName || stripFieldFormat(initialValue?.left) || '',
      operator: initialValue?.operator || defaultOperator,
      right: getInitialRightValue(),
    },
  });

  const formValues = watch();

  const isCustomField = useMemo(() => {
    if (!allowCustomFields) return false;
    if (!formValues.left && !initialValue?.left) return false;
    
    const leftValue = formValues.left || initialValue?.left;
    if (!leftValue) return false;
    
    if (!fieldMetadata || fieldMetadata.length === 0) return false;
    
    const strippedLeft = stripFieldFormat(leftValue).toLowerCase();
    const existsInMetadata = fieldMetadata.some((f) => f.fieldName?.toLowerCase() === strippedLeft);
    
    return !existsInMetadata;
  }, [allowCustomFields, formValues.left, initialValue?.left, fieldMetadata]);

  const lastInitialValueRef = useRef<string>('');
  useEffect(() => {
    const initialValueKey = JSON.stringify(initialValue);
    const formLeftNormalized = stripFieldFormat(formValues.left).toLowerCase();
    const initialLeftNormalized = stripFieldFormat(initialValue?.left).toLowerCase();

    if (formLeftNormalized && initialLeftNormalized && formLeftNormalized !== initialLeftNormalized) {
      return;
    }

    if (lastInitialValueRef.current !== initialValueKey) {
      const currentIsList = initialIsList;
      let newRightValue: string | number | string[] | Date;
      
      if (currentIsList) {
        newRightValue = initialValue?.right?.map(v => stripRightValueFormat(v)) || [];
      } else {
        let rawValue = stripRightValueFormat(initialValue?.right?.[0]) || '';
        
        if (initialFieldMetadata && (initialFieldMetadata.dataType === DataType.Number || initialFieldMetadata.dataType === DataType.Integer)) {
          rawValue = rawValue.replace(/^\[/, '').replace(/\]$/, '').trim();
          const numValue = parseFloat(rawValue);
          newRightValue = isNaN(numValue) ? rawValue : numValue;
        } else if (initialFieldMetadata && (initialFieldMetadata.dataType === DataType.Date || initialFieldMetadata.dataType === DataType.DateTime)) {
          rawValue = rawValue.replace(/^\[/, '').replace(/\]$/, '').trim();
          newRightValue = initialFieldMetadata.dataType === DataType.Date
            ? parseDateOnlyAsLocal(rawValue)
            : (() => { const d = new Date(rawValue); return isNaN(d.getTime()) ? rawValue : d; })();
        } else {
          newRightValue = /^\d{4}-\d{2}-\d{2}/.test(rawValue)
            ? (() => { const d = parseDateOnlyAsLocal(rawValue); return d instanceof Date ? d : rawValue; })()
            : rawValue;
        }
      }
      
      const newValues = {
        left: initialFieldMetadata?.fieldName || stripFieldFormat(initialValue?.left) || '',
        operator: initialValue?.operator || defaultOperator,
        right: newRightValue,
      };
      reset(newValues);
      lastInitialValueRef.current = initialValueKey;
    }
  }, [initialValue, reset, defaultOperator, initialIsList, initialFieldMetadata, formValues.left]);

  const prevLeftRef = useRef<string>('');
  const prevOperatorRef = useRef<string>('');
  useEffect(() => {
    if (!formValues.left || !operators || !fieldMetadata) {
      return;
    }

    const leftLower = formValues.left.toLowerCase();
    const field = fieldMetadata.find((f) => f.fieldName?.toLowerCase() === leftLower);


    const prevLeftLower = stripFieldFormat(prevLeftRef.current).toLowerCase();
    if (prevLeftLower && prevLeftLower !== leftLower) {
      setValue('right', field?.isList ? [] : '', { shouldValidate: true });
      prevOperatorRef.current = formValues.operator;
    }
    prevLeftRef.current = formValues.left;

    if (!field) {
      return;
    }

    const isMultiValueOperator = formValues.operator === 'IsOneOf' || 
                                  formValues.operator === 'ContainsAny' || 
                                  formValues.operator === 'ContainsAll';
    const shouldBeList = field.isList || isMultiValueOperator;
    const currentIsList = Array.isArray(formValues.right);

    if (prevOperatorRef.current !== formValues.operator) {
      if (shouldBeList && !currentIsList) {
        setValue('right', [], { shouldValidate: true });
      } else if (!shouldBeList && currentIsList) {
        setValue('right', '', { shouldValidate: true });
      }
      prevOperatorRef.current = formValues.operator;
    }
  }, [formValues.operator, formValues.left, fieldMetadata, operators, formValues.right, setValue]);

  useEffect(() => {
    if (!formValues.left || !operators || !fieldMetadata) {
      return;
    }

    const leftLower = formValues.left.toLowerCase();
    const field = fieldMetadata.find((f) => f.fieldName?.toLowerCase() === leftLower);
    if (!field) {
      return;
    }

    const currentOperator = operators.find((op) => op.operator === formValues.operator);
    const isValidOperator =
      currentOperator &&
      currentOperator.isLogical &&
      (currentOperator.validDataTypes?.includes(field.dataType) ?? true);

    if (!isValidOperator) {
      let validOperators = operators.filter(
        (op) => op.isLogical && (op.validDataTypes?.includes(field.dataType) ?? true)
      );
      
      if (field.dataType === DataType.TrueFalse) {
        validOperators = validOperators.filter(
          (op) => op.operator === 'Equal' || op.operator === 'NotEqual'
        );
      }

      if (validOperators.length > 0) {
        setValue('operator', validOperators[0].operator);
      }
    }
  }, [formValues.left, formValues.operator, operators, fieldMetadata, setValue]);
  
  // console.log('[SimpleExpressionForm] formValues:', formValues);
  // console.log('[SimpleExpressionForm] fieldMetadata:', fieldMetadata);
  
  const fieldOptions = useMemo(() => {
    if (!fieldMetadata || fieldMetadata.length === 0) {
      // console.log('[SimpleExpressionForm] No fieldMetadata, returning empty options');
      return [];
    }
    const options = fieldMetadata.map((field) => ({
      label: field.fieldName || '',
      value: field.fieldName || '',
    }));
    // console.log('[SimpleExpressionForm] Generated fieldOptions:', options);
    return options;
  }, [fieldMetadata]);

  const selectedField = useMemo(() => {
    if (!formValues.left || !fieldMetadata) {
      return null;
    }
    const leftLower = formValues.left.toLowerCase();
    return fieldMetadata.find((f) => f.fieldName?.toLowerCase() === leftLower) || null;
  }, [formValues.left, fieldMetadata]);

  const selectedFieldDataType = selectedField?.dataType || null;
  const isSelectedFieldList = selectedField?.isList || false;

  const [enumValues, setEnumValues] = useState<EnumValueData[]>([]);
  const [isLoadingEnumValues, setIsLoadingEnumValues] = useState(false);

  useEffect(() => {
    const fetchEnumValues = async () => {
      if (!selectedField || !selectedField.enumType || selectedField.dataType !== DataType.Enumeration) {
        setEnumValues([]);
        return;
      }

      setIsLoadingEnumValues(true);
      try {
        const values = await commonService.getEnumValues(selectedField.enumType);
        setEnumValues(values.filter((v) => !v.isHidden).sort((a, b) => a.order - b.order));
      } catch (error) {
        console.error('Failed to fetch enum values:', error);
        setEnumValues([]);
      } finally {
        setIsLoadingEnumValues(false);
      }
    };

    fetchEnumValues();
  }, [selectedField]);

  useEffect(() => {
    if (onChange) {
      const hasLeft = formValues.left?.trim();
      const hasOperator = formValues.operator;
      const isMultiValueOperator = formValues.operator === 'IsOneOf' || 
                                     formValues.operator === 'ContainsAny' || 
                                     formValues.operator === 'ContainsAll';
      const expectsArray = isSelectedFieldList || isMultiValueOperator;
      
      const hasRight = expectsArray
        ? Array.isArray(formValues.right) && formValues.right.length > 0
        : formValues.right != null && formValues.right !== '' && (typeof formValues.right === 'string' ? formValues.right.trim() : true);

      if (hasLeft && hasOperator && hasRight) {
        let rightValues: string[];
        if (expectsArray) {
          if (Array.isArray(formValues.right)) {
            rightValues = formValues.right.map(v => formatRightValueForBackend(v));
          } else {
            rightValues = [formatRightValueForBackend(formValues.right)];
          }
        } else {
          if (typeof formValues.right === 'number') {
            rightValues = [String(formValues.right)];
          } else if (formValues.right instanceof Date) {
            const dateVal = formValues.right as Date;
            const isDateOnly = selectedFieldDataType === DataType.Date;
            if (isDateOnly) {
              const y = dateVal.getFullYear();
              const m = String(dateVal.getMonth() + 1).padStart(2, '0');
              const d = String(dateVal.getDate()).padStart(2, '0');
              rightValues = [`${y}-${m}-${d}`];
            } else {
              rightValues = [formatRightValueForBackend(dateVal)];
            }
          } else if (typeof formValues.right === 'string') {
            rightValues = [formatRightValueForBackend(formValues.right)];
          } else {
            rightValues = [];
          }
        }

        const expression = {
          left: formatFieldForBackend(formValues.left),
          operator: formValues.operator as Operator,
          right: rightValues,
        } as SimpleBinaryExpression;
        // console.log('[SimpleExpressionForm] Calling onChange with expression:', expression);
        onChange(expression);
      } else if (hasLeft && hasOperator) {
        onChange({
          left: formatFieldForBackend(formValues.left),
          operator: formValues.operator as Operator,
          right: [],
        } as SimpleBinaryExpression);
      } else {
        onChange(null);
      }
    }
  }, [formValues.left, formValues.operator, formValues.right, selectedFieldDataType]);

  const operatorOptions = useMemo(() => {
    if (!operators || operators.length === 0) {
      return [];
    }
    
    let filteredOperators = operators;
    
    if (selectedFieldDataType) {
      filteredOperators = operators.filter(
        (op) => op.isLogical && (op.validDataTypes?.includes(selectedFieldDataType) ?? true)
      );
      
      if (selectedFieldDataType === DataType.TrueFalse) {
        filteredOperators = filteredOperators.filter(
          (op) => op.operator === 'Equal' || op.operator === 'NotEqual'
        );
      }
    } else {
      filteredOperators = operators.filter((op) => op.isLogical);
    }
    
    const options = filteredOperators.map((op) => ({
      label: op.name || op.operator,
      value: op.operator,
    }));
    return options;
  }, [operators, selectedFieldDataType]);

  const ensureCorrectValueType = ({ expectedType }: { expectedType: 'array' | 'value' }): boolean => {
    const isCurrentlyArray = Array.isArray(formValues.right);
  
    const shouldBeConvertedToArray = expectedType === 'array' && !isCurrentlyArray;
    const shouldBeConvertedToValue = expectedType === 'value' && isCurrentlyArray;
  
    if (shouldBeConvertedToArray || shouldBeConvertedToValue) {
      setValue('right', expectedType === 'array' ? [] : '', { shouldValidate: false });
      return false;
    }
    
    return true;
  };

  return (
    <div className={`p-fluid p-input-filled flex gap-2 text-sm w-full ${styles.form}`}>
      <div className="min-w-42 flex-1">
        <FormDropdown
          control={control}
          errors={errors}
          field="left"
          label=""
          options={fieldOptions}
          optionLabel="label"
          optionValue="value"
          placeholder="Select field"
          showFilter={true}
          className="mb-0"
        />
      </div>

      <div className="w-42">
        <FormDropdown
          control={control}
          errors={errors}
          field="operator"
          label=""
          options={operatorOptions}
          optionLabel="label"
          optionValue="value"
          showFilter={true}
          className="mb-0"
        />
      </div>

      <div className="min-w-42 flex-1">
        {(() => {
          if (isCustomField) {
            return (
              <FormInputText
                control={control}
                errors={errors}
                field="right"
                label=""
                placeholder="Value"
                className="mb-0"
              />
            );
          }
          
          if (
            (formValues.operator === 'IsOneOf' || 
             formValues.operator === 'ContainsAny' || 
             formValues.operator === 'ContainsAll') &&
            selectedFieldDataType === DataType.Enumeration &&
            enumValues.length > 0
          ) {
            if (!ensureCorrectValueType({ expectedType: 'array' })) return null;
            
            return (
              <FormMultiSelect
                control={control}
                errors={errors}
                field="right"
                label=""
                options={enumValues.map((enumVal) => ({
                  label: enumVal.displayName || enumVal.name || '',
                  value: enumVal.value,
                }))}
                optionLabel="label"
                optionValue="value"
                placeholder={isLoadingEnumValues ? 'Loading...' : 'Select values'}
                showFilter={true}
                className="mb-0"
                disabled={isLoadingEnumValues}
              />
            );
          }
          
          if (
            (formValues.operator === 'IsOneOf' || 
             formValues.operator === 'ContainsAny' || 
             formValues.operator === 'ContainsAll') &&
            (selectedFieldDataType === DataType.Text || selectedFieldDataType === DataType.Enumeration)
          ) {
            if (!ensureCorrectValueType({ expectedType: 'array' })) return null;
            
            return (
              <FormChips
                control={control}
                errors={errors}
                field="right"
                label=""
                placeholder="Type and press Enter or comma"
                className="mb-0"
                separator=","
              />
            );
          }
          
          if (selectedFieldDataType === DataType.TrueFalse && !isSelectedFieldList) {
            if (!ensureCorrectValueType({ expectedType: 'value' })) return null;
            
            return (
              <FormDropdown
                control={control}
                errors={errors}
                field="right"
                label=""
                options={[
                  { label: 'True', value: 'TRUE' },
                  { label: 'False', value: 'FALSE' }
                ]}
                optionLabel="label"
                optionValue="value"
                placeholder="Select True or False"
                className="mb-0"
              />
            );
          }
          
          if ((selectedFieldDataType === DataType.Number || selectedFieldDataType === DataType.Integer) && !isSelectedFieldList) {
            if (!ensureCorrectValueType({ expectedType: 'value' })) return null;
            
            return (
              <FormInputNumber control={control} errors={errors} field="right" label="" placeholder="Value" className="mb-0" />
            );
          }
          
          if ((selectedFieldDataType === DataType.Date || selectedFieldDataType === DataType.DateTime) && !isSelectedFieldList) {
            if (!ensureCorrectValueType({ expectedType: 'value' })) return null;
            
            return (
              <FormCalendar
                control={control}
                errors={errors}
                field="right"
                label=""
                placeholder="Select date"
                className="mb-0"
                showTime={selectedFieldDataType === DataType.DateTime}
              />
            );
          }
          
          if (selectedFieldDataType === DataType.Enumeration && !isSelectedFieldList && enumValues.length > 0) {
            if (!ensureCorrectValueType({ expectedType: 'value' })) return null;
            
            return (
              <FormDropdown
                control={control}
                errors={errors}
                field="right"
                label=""
                options={enumValues.map((enumVal) => ({
                  label: enumVal.displayName || enumVal.name || '',
                  value: enumVal.value,
                }))}
                optionLabel="label"
                optionValue="value"
                placeholder={isLoadingEnumValues ? 'Loading...' : 'Select value'}
                className="mb-0"
                disabled={isLoadingEnumValues}
                showFilter={true}
              />
            );
          }
          
          if ((selectedFieldDataType === DataType.Text || selectedFieldDataType === DataType.Enumeration) && !isSelectedFieldList) {
            if (!ensureCorrectValueType({ expectedType: 'value' })) return null;
            
            return (
              <FormInputText control={control} errors={errors} field="right" label="" placeholder="Value" className="mb-0" />
            );
          }
          
          if (isSelectedFieldList && rightFieldMetadata && rightFieldMetadata.length > 0) {
            if (!ensureCorrectValueType({ expectedType: 'array' })) return null;
            
            return (
              <FormMultiSelect
                control={control}
                errors={errors}
                field="right"
                label=""
                options={rightFieldMetadata.map((field) => ({
                  label: field.fieldName || '',
                  value: field.fieldName || '',
                }))}
                optionLabel="label"
                optionValue="value"
                placeholder="Select fields"
                showFilter={true}
                className="mb-0"
              />
            );
          }
          
          if (rightFieldMetadata && rightFieldMetadata.length > 0) {
            if (!ensureCorrectValueType({ expectedType: 'value' })) return null;
            
            return (
              <FormDropdown
                control={control}
                errors={errors}
                field="right"
                label=""
                options={rightFieldMetadata.map((field) => ({
                  label: field.fieldName || '',
                  value: field.fieldName || '',
                }))}
                optionLabel="label"
                optionValue="value"
                placeholder="Select field or enter value"
                showFilter={true}
                className="mb-0"
              />
            );
          }

          if (!ensureCorrectValueType({ expectedType: 'value' })) return null;
          
          return (
            <FormInputText control={control} errors={errors} field="right" label="" placeholder="Value" className="mb-0" />
          );
        })()}
      </div>

      {onAddGroup && showAddGroup && (
        <Button
          type="button"
          icon="pi pi-plus"
          className="p-button-text"
          onClick={onAddGroup}
          tooltip="Create nested group"
          style={{ height: '32.25px', minWidth: '32.25px', padding: 0, margin: 0, alignSelf: 'flex-start' }}
        />
      )}

      {onCancel && showDelete && (
        <Button 
          type="button" 
          icon="pi pi-trash" 
          className="p-button-text p-button-danger" 
          onClick={onCancel}
          style={{ height: '32.25px', minWidth: '32.25px', padding: 0, margin: 0, alignSelf: 'flex-start' }}
        />
      )}
    </div>
  );
};
